/**
 * §4.11 surface-elision canonicalization — the formatter's reference-rewrite
 * pass (v0.10.1).
 *
 * Rewrites interface REFERENCES to the short form: a reference that spells a
 * module prefix + the full surface path + leaf (`Orders.OrdersResource.create`)
 * is shortened to `Module.leaf` (`Orders.create`) whenever the leaf names
 * exactly one interface across that module's surface tree and the short form
 * has no competing interpretation (no direct interface, no submodule, no other
 * same-spelling interface). The module prefix is kept exactly as written — only
 * the surface segments are elided.
 *
 * Scope is FILE-LOCAL (spec §4.11: "only in the file it is formatting", like
 * the trailing dot §4.13.2): the pass parses + resolves the single file being
 * formatted, so only modules declared in that file can prove elidability and
 * only references in that file are rewritten. A collision introduced elsewhere
 * surfaces later as the AMBIGUOUS_INTERFACE_LEAF diagnostic at the site, for
 * the author to qualify.
 *
 * Conservative by construction: a file with parse errors is left untouched
 * (structure untrustworthy), a rewrite is applied only when the span's current
 * text equals the recorded written reference, and elidability is decided by the
 * engine's own spelling universe (`buildIfaceCandidates`) so the formatter can
 * never produce a form the resolver reads differently.
 */

import {
  parse,
  resolveFile,
  forEachCallHop,
  buildIfaceCandidates,
  buildModuleCandidates,
  type ResolvedModel,
  type ResolvedModule,
  type ResolvedSurface,
} from "@archlang/engine";

/** Where each interface lives: its owning MODULE path + surface path + leaf. */
interface IfaceHome {
  readonly moduleSegs: readonly string[];
  readonly surfaceSegs: readonly string[];
  readonly leaf: string;
}

function indexIfaceHomes(model: ResolvedModel): Map<string, IfaceHome> {
  const out = new Map<string, IfaceHome>();
  const visitSurface = (
    surfaces: readonly ResolvedSurface[],
    surfaceSegs: readonly string[],
    moduleSegs: readonly string[]
  ): void => {
    for (const f of surfaces) {
      const segs = [...surfaceSegs, f.name];
      for (const i of f.interfaces) {
        out.set([...moduleSegs, ...segs, i.name].join("."), { moduleSegs, surfaceSegs: segs, leaf: i.name });
      }
      visitSurface(f.surfaces, segs, moduleSegs);
    }
  };
  const visitModule = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    for (const i of m.interfaces) {
      out.set([...path, i.name].join("."), { moduleSegs: path, surfaceSegs: [], leaf: i.name });
    }
    visitSurface(m.surfaces, [], path);
    for (const child of m.modules) visitModule(child, path);
  };
  for (const m of model.modules) visitModule(m, []);
  return out;
}

/** True iff `suffix` is a segment-aligned suffix of `path`. */
function isSegSuffix(suffix: readonly string[], path: readonly string[]): boolean {
  if (suffix.length === 0 || suffix.length > path.length) return false;
  const off = path.length - suffix.length;
  return suffix.every((s, i) => s === path[off + i]);
}

interface Rewrite {
  readonly start: number;
  readonly end: number;
  readonly from: string;
  readonly to: string;
}

/**
 * Canonicalize §4.11 surface elisions in `text`. Returns the rewritten text,
 * or the input unchanged when there is nothing to elide (or the file does not
 * parse cleanly).
 */
export function canonicalizeElisions(text: string): string {
  const parsed = parse(text, "format://elision");
  if (parsed.errors.length > 0) return text;

  const { model } = resolveFile(parsed.file);
  const homes = indexIfaceHomes(model);
  if (homes.size === 0) return text;
  const ifaceCands = buildIfaceCandidates(model);
  const moduleCands = buildModuleCandidates(model.modules);

  /** The sole declared interface a written spelling names, or undefined. */
  const soleTarget = (spelling: string): string | undefined => {
    const cands = ifaceCands.get(spelling);
    if (!cands) return undefined;
    const distinct = [...new Set(cands.map((c) => c.value))];
    return distinct.length === 1 ? distinct[0] : undefined;
  };

  const rewrites = new Map<number, Rewrite>(); // keyed by start offset (dedupes template-stamped IR copies)
  const consider = (written: string, span: { start: { offset: number }; end: { offset: number } } | undefined): void => {
    if (!span || written.includes(" ")) return;
    const target = soleTarget(written);
    if (!target) return; // unresolved or ambiguous as written — not ours to touch
    const home = homes.get(target);
    if (!home || home.surfaceSegs.length === 0) return; // direct interface — nothing to elide
    // The written form must spell <module-suffix>.<full surface path>.<leaf> —
    // partial spellings (already-short, or surface-suffix hoists) are left alone.
    const W = written.split(".");
    const tail = [...home.surfaceSegs, home.leaf];
    if (W.length <= tail.length) return; // no module prefix present
    if (!isSegSuffix(tail, W)) return;
    const prefix = W.slice(0, W.length - tail.length);
    if (!isSegSuffix(prefix, home.moduleSegs)) return; // prefix must name the owning module
    const short = [...prefix, home.leaf].join(".");
    // The short form must read back as EXACTLY this interface (unique leaf in
    // the module's surface tree; no same-name direct interface elsewhere under
    // this spelling) and must not name an exact module path (a submodule would
    // claim it via the module ladder).
    if (soleTarget(short) !== target) return;
    if (moduleCands.get(short)?.includes(short)) return;
    const start = span.start.offset;
    const end = span.end.offset;
    if (text.slice(start, end) !== written) return; // span drift — never guess
    rewrites.set(start, { start, end, from: written, to: short });
  };

  for (const pm of model.processModels ?? []) {
    forEachCallHop(pm.body, (hop) => {
      if (hop.callee.anonymous) return; // module callee / trailing dot — no leaf
      consider(hop.callee.text.text, hop.callee.text.source);
    });
  }
  for (const sm of model.subprocessModels ?? []) {
    forEachCallHop(sm.body, (hop) => {
      if (hop.callee.anonymous) return;
      consider(hop.callee.text.text, hop.callee.text.source);
    });
  }

  if (rewrites.size === 0) return text;
  const ordered = [...rewrites.values()].sort((a, b) => b.start - a.start);
  let out = text;
  for (const r of ordered) {
    out = out.slice(0, r.start) + r.to + out.slice(r.end);
  }
  return out;
}
